import { useState } from 'react';
import type { Nullable } from 'corgi-types';
import type { Challenge, GQLLocale, MutationLoginWithEmailAndPasswordArgs } from 'graphql-ts-types';
import { ChallengeType, LoginFlow } from 'graphql-ts-types';

import cookieStorage from 'common/CookieStorage';
import { CheckboxValue } from 'common/enums/form';
import { executeQuery } from 'common/gql';
import { useGQLLocale } from 'common/hooks/locale';
import { useLocalization } from 'common/providers/LocaleProvider';
import type { GraphQLExtensions } from 'common/types/graphql';
import { isErrorWithExtensions } from 'common/utils/error-handlers/error-with-extensions';
import { extractErrorMessage } from 'common/utils/error-handlers/gql-extensions';
import { captureError } from 'common/utils/error-reporter';
import { getReCaptchaChallengeToken, ReCaptchaAction } from 'common/utils/grecaptcha';
import performLoginMutation from '../../gql/mutation/perform-login.gql';
import type { PerformLoginResponse } from '../../gql/types';
import type { FieldName } from '../enums';
import { trackLoginError, trackLoginEvent } from '../tracking';

interface UsePerformLoginHandlerOptions {
  setCommonError: (errorMessage: Nullable<string>) => void;
  onSuccess: () => void;
}

const performLogin = ({
  locale,
  email,
  password,
  isRememberMeChecked,
  challenge,
}: {
  locale: GQLLocale;
  email: string;
  password: string;
  isRememberMeChecked: boolean;
  challenge: Challenge;
}) =>
  executeQuery<
    PerformLoginResponse,
    Pick<
      MutationLoginWithEmailAndPasswordArgs,
      'locale' | 'email' | 'password' | 'flow' | 'challenge'
    >
  >({
    query: performLoginMutation,
    variables: {
      locale,
      email,
      password,
      flow: isRememberMeChecked ? LoginFlow.RememberMe : LoginFlow.WebsiteSession,
      challenge,
    },
  });

export const usePerformLoginHandler = ({
  setCommonError,
  onSuccess,
}: UsePerformLoginHandlerOptions) => {
  const locale = useGQLLocale();
  const { t } = useLocalization();
  const [isSuccessful, setIsSuccessful] = useState(false);

  const performLoginHandler = async ({
    emailAddress: email,
    password,
    rememberMe,
  }: { [Key in FieldName]: string }) => {
    const isRememberMeChecked = rememberMe === CheckboxValue.True;

    setCommonError(null);

    // In case reCaptcha is not available, we still want to be able to attempt a login
    // with a dummy token. This is why we set a token value here.
    // This login attempt would most likely fail, but in case of reCaptcha issues,
    // the token checks could quickly be disabled on the backend side.
    let reCaptchaToken = 'error';
    try {
      reCaptchaToken = await getReCaptchaChallengeToken(ReCaptchaAction.Login);
    } catch (error) {
      captureError(`Getting reCaptcha token failed. ${error}`);
    }

    try {
      const challenge = {
        type: ChallengeType.ReCaptcha,
        payload: reCaptchaToken,
      };

      const {
        data: { user },
      } = await performLogin({ locale, email, password, isRememberMeChecked, challenge });
      cookieStorage.setZipCode(user.zipCode);
      trackLoginEvent(user);
      setIsSuccessful(true);
      onSuccess();

      return true;
    } catch (error) {
      const extensions: GraphQLExtensions = isErrorWithExtensions(error) ? error.extensions : {};
      const message = extractErrorMessage(extensions, t);
      trackLoginError(message);
      setCommonError(message);

      return false;
    }
  };

  return { isSuccessful, performLoginHandler };
};
